import type { DemoSession } from "./demoAuth";
import { getAssignedDepartmentId } from "./demoAuth";
import { getCommercialPlan } from "./billing/plans";
import type { WorkspaceStoreState } from "./workspaceStore";
import type { AdminAuditLog, Department, DepartmentResponse, Workspace } from "../types";

type ModuleEntitlementLike = {
  planId?: string;
  status?: string;
  accessAllowed?: boolean;
  productiveAccessAllowed?: boolean;
};

export interface WorkspaceModuleAccess {
  hasGuidedAccess: boolean;
  hasDealWinAccess: boolean;
  canManageDepartments: boolean;
  canUseCustomerValueCalculation: boolean;
  canUseDealWinEngine: boolean;
  canManageDealWinLicenses: boolean;
}

function isActiveEntitlement(entitlement: ModuleEntitlementLike | undefined): boolean {
  if (!entitlement) return false;
  if (entitlement.accessAllowed || entitlement.productiveAccessAllowed) return true;
  return entitlement.status === "active" || entitlement.status === "expiring" || entitlement.status === "cancelled_active_until_period_end";
}

export function deriveWorkspaceModuleAccess(input: {
  entitlement?: ModuleEntitlementLike;
  entitlements?: ModuleEntitlementLike[];
  packageType?: string;
  productiveAccessAllowed?: boolean;
  accessAllowed?: boolean;
}): WorkspaceModuleAccess {
  const activeEntitlements = [
    ...(input.entitlements ?? []),
    ...(input.entitlement ? [input.entitlement] : [])
  ].filter(isActiveEntitlement);
  const effectivePackageAccess = Boolean(input.productiveAccessAllowed ?? input.accessAllowed);
  const moduleSet = new Set(activeEntitlements
    .map((entitlement) => getCommercialPlan(entitlement.planId)?.module)
    .filter(Boolean));

  if (moduleSet.size === 0 && effectivePackageAccess) {
    const packageModule = getCommercialPlan(input.packageType)?.module;
    if (packageModule) moduleSet.add(packageModule);
  }

  const hasGuidedAccess = moduleSet.has("guided_digital_access");
  const hasDealWinAccess = moduleSet.has("deal_win_engine");

  return {
    hasGuidedAccess,
    hasDealWinAccess,
    canManageDepartments: hasGuidedAccess,
    canUseCustomerValueCalculation: hasGuidedAccess,
    canUseDealWinEngine: hasDealWinAccess,
    canManageDealWinLicenses: hasDealWinAccess
  };
}

export function canSessionAccessWorkspace(
  state: WorkspaceStoreState,
  session: DemoSession | undefined,
  workspace: Workspace | undefined
): boolean {
  if (!session || !workspace) return false;
  if (session.role === "hauffe_admin") return true;
  if (session.role !== "company_owner") return false;

  const ownerUser = state.users.find((user) => user.id === workspace.createdByUserId);
  if (ownerUser?.email.toLowerCase() === session.email.toLowerCase()) return true;
  if (ownerUser?.organizationId && ownerUser.organizationId === sessionToOrganizationId(session)) return true;
  return workspace.organizationId === sessionToOrganizationId(session);
}

export function canSessionAccessDepartmentResponse(
  state: WorkspaceStoreState,
  session: DemoSession | undefined,
  department: Department | undefined
): boolean {
  if (!session || !department) return false;
  if (session.role === "hauffe_admin") return true;
  if (session.role === "department_contributor") {
    return department.participantUserId === session.userId || getAssignedDepartmentId(session) === department.id;
  }

  const workspace = state.workspaces.find((item) => item.id === department.workspaceId);
  return canSessionAccessWorkspace(state, session, workspace);
}

export function hasDepartmentResponse(response: DepartmentResponse | undefined): boolean {
  return Boolean(response && Object.values(response.answers).some((answer) => answer.trim().length > 0));
}

export function formatAuditAction(action: string): string {
  const labels: Record<string, string> = {
    workspace_set_draft: "Workspace vorbereitet",
    workspace_activated: "Workspace aktiviert",
    workspace_paused: "Workspace pausiert",
    workspace_locked: "Workspace gesperrt",
    workspace_expired: "Workspace abgelaufen",
    workspace_archived: "Workspace archiviert",
    workspace_extended: "Laufzeit verlängert",
    package_upgraded: "Paket geändert",
    package_downgraded: "Paket geändert",
    department_limit_changed: "Abteilungslimit geändert",
    owner_changed: "Company Owner geändert",
    department_reopened: "Abteilung erneut geöffnet",
    invite_regenerated: "Invite-Link neu erzeugt"
  };

  return labels[action] ?? action.replace(/_/g, " ");
}

export function formatAuditDetails(log: AdminAuditLog): string {
  try {
    const details = JSON.parse(log.detailsJson) as Record<string, unknown>;
    if (log.action === "workspace_extended") {
      return `Von ${String(details.previousEndDate ?? "-")} auf ${String(details.nextEndDate ?? "-")}.`;
    }
    if (log.action === "department_limit_changed") {
      return `Von ${String(details.previousLimit ?? "-")} auf ${String(details.nextLimit ?? "-")} Abteilungen.`;
    }
    if (log.action === "owner_changed") {
      return `Neuer Owner: ${String(details.nextOwnerEmail ?? "nicht angegeben")}.`;
    }
    if (log.action === "department_reopened") {
      return `Abteilung ${String(details.departmentId ?? "-")} wurde wieder zur Bearbeitung geöffnet.`;
    }
    if (log.action === "invite_regenerated") {
      return `Neuer Invite-Link für ${String(details.departmentId ?? "-")} erzeugt.`;
    }
    if (log.action.startsWith("workspace_")) {
      return `Status von ${String(details.previousStatus ?? "-")} auf ${String(details.nextStatus ?? "-")} geändert.`;
    }
    if (log.action.startsWith("package_")) {
      return `Von ${String(details.previousPackage ?? "-")} auf ${String(details.nextPackage ?? "-")}.`;
    }
    return "Aktion wurde protokolliert.";
  } catch {
    return "Details konnten nicht gelesen werden.";
  }
}

function sessionToOrganizationId(session: DemoSession): string | undefined {
  if (session.role === "hauffe_admin") return undefined;
  return "org-hauffe-demo";
}
